package com.example.crashbit

import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.graphics.Color
import kotlinx.coroutines.*
import kotlin.math.*
import kotlin.random.Random

data class MergerEvent(
    val position: Offset,
    val totalMass: Float,
    val velocity: Offset = Offset.Zero,
    val didCreateStar: Boolean = false,
    val didCreateBlackHole: Boolean = false,
    val isStellarEvent: Boolean = false,
    val consumedIds: List<Long> = emptyList(),
    val producedIds: List<Long> = emptyList(),
    val lostJordyPopulation: Long = 0L
)

data class SimulationConfig(
    val g: Float = 2500f,
    val mergerPassLimit: Int = 15,
    val stellarDamping: Float = 0.998f,
    val evolutionSpeed: Float = 1.0f,
    val auLimit: Float = 10000f,
    val planetsCanBeConsumed: Boolean = true,
    val starsCanMerge: Boolean = true,
    val starsAreDynamic: Boolean = true,
    val wormholes: List<Wormhole> = emptyList()
)

data class OracleResult(
    val paths: List<List<Offset>>,
    val collisions: List<Offset>
)

class QuadNode {
    var bounds: Rect = Rect.Zero
    var mass: Float = 0f
    var centerOfMass: Offset = Offset.Zero
    var body: SpaceBody? = null
    var children: Array<QuadNode?>? = null
    var isLeaf: Boolean = true
    companion object { const val MAX_DEPTH = 12 }
    fun reset(b: Rect) { bounds = b; mass = 0f; centerOfMass = Offset.Zero; body = null; isLeaf = true; if (children != null) { for (i in 0..3) children!![i] = null } }
    fun insert(p: SpaceBody, pool: QuadNodePool, depth: Int = 0) {
        if (mass == 0f && isLeaf) { body = p; mass = p.mass; centerOfMass = p.position; return }
        if (isLeaf && depth < MAX_DEPTH) {
            val oldBody = body; body = null; isLeaf = false; if (children == null) children = arrayOfNulls(4)
            val midX = bounds.left + bounds.width / 2f; val midY = bounds.top + bounds.height / 2f
            children!![0] = pool.get().apply { reset(Rect(bounds.left, bounds.top, midX, midY)) }; children!![1] = pool.get().apply { reset(Rect(midX, bounds.top, bounds.right, midY)) }; children!![2] = pool.get().apply { reset(Rect(bounds.left, midY, midX, bounds.bottom)) }; children!![3] = pool.get().apply { reset(Rect(midX, midY, bounds.right, bounds.bottom)) }
            if (oldBody != null) insertToChild(oldBody, pool, depth); insertToChild(p, pool, depth)
        } else if (!isLeaf) insertToChild(p, pool, depth)
        val totalM = mass + p.mass; centerOfMass = (centerOfMass * mass + p.position * p.mass) / (totalM).coerceAtLeast(1.0f); mass = totalM
    }
    private fun insertToChild(p: SpaceBody, pool: QuadNodePool, depth: Int) {
        val midX = bounds.left + bounds.width / 2f; val midY = bounds.top + bounds.height / 2f; val idx = (if (p.position.x >= midX) 1 else 0) + (if (p.position.y >= midY) 2 else 0); children!![idx]?.insert(p, pool, depth + 1)
    }
    fun calculateAcceleration(p: SpaceBody, theta: Float, G: Float): Offset {
        if (body?.id == p.id || mass == 0f) return Offset.Zero
        val dx = centerOfMass.x - p.position.x; val dy = centerOfMass.y - p.position.y; val dSq = dx * dx + dy * dy; val dist = sqrt(dSq)
        if (dist < 1f) return Offset.Zero
        if (isLeaf || (bounds.width / dist < theta)) {
            val effM = if (dist < 20f && mass > p.mass) mass - p.mass else mass; if (effM <= 0f) return Offset.Zero
            val hasBH = p.isBlackHole || (body?.isBlackHole ?: false)
            val soft = if (hasBH) 1.0f else 250f; val effG = if (hasBH) G * 2.5f else G
            val factor = (effG * effM) / (dSq + soft).pow(1.5f); return Offset(dx * factor, dy * factor)
        } else { var ax = 0f; var ay = 0f; children?.forEach { c -> val a = c?.calculateAcceleration(p, theta, G) ?: Offset.Zero; ax += a.x; ay += a.y }; return Offset(ax, ay) }
    }
}

class QuadNodePool(size: Int = 40000) {
    private var pool = Array(size) { QuadNode() }; private var index = 0
    fun get(): QuadNode { if (index >= pool.size) { val newPool = Array(pool.size + 10000) { QuadNode() }; pool.copyInto(newPool); pool = newPool }; return pool[index++] }
    fun clear() { index = 0 }
}

class PhysicsPrimitiveBuffer(capacity: Int = 2048) {
    var count = 0
    var posX = FloatArray(capacity)
    var posY = FloatArray(capacity)
    var velX = FloatArray(capacity)
    var velY = FloatArray(capacity)
    var mass = FloatArray(capacity)
    var radius = FloatArray(capacity)

    fun ensureCapacity(needed: Int) {
        if (needed > posX.size) {
            val newCap = (needed * 1.5f).toInt().coerceAtLeast(1024)
            posX = posX.copyOf(newCap)
            posY = posY.copyOf(newCap)
            velX = velX.copyOf(newCap)
            velY = velY.copyOf(newCap)
            mass = mass.copyOf(newCap)
            radius = radius.copyOf(newCap)
        }
    }

    fun syncFrom(bodies: List<SpaceBody>) {
        count = bodies.size
        ensureCapacity(count)
        for (i in 0 until count) {
            val b = bodies[i]
            posX[i] = b.position.x
            posY[i] = b.position.y
            velX[i] = b.velocity.x
            velY[i] = b.velocity.y
            mass[i] = b.mass
            radius[i] = b.radius
        }
    }

    fun syncTo(bodies: List<SpaceBody>) {
        for (i in 0 until bodies.size.coerceAtMost(count)) {
            val b = bodies[i]
            b.position = Offset(posX[i], posY[i])
            b.velocity = Offset(velX[i], velY[i])
        }
    }
}

object PhysicsEngine {
    const val G = 2500f
    const val BLACK_HOLE_LIMIT = 2000000f; const val STELLAR_EVOLUTION_LIMIT = 500000f; const val PASSIVE_ACCRETION_RATE = 500f; const val STAR_ACCRETION_RATE = 200f; const val HEAVY_PLANET_THRESHOLD = 400f; const val THETA = 0.5f; const val HABITABLE_MIN = 400f; const val HABITABLE_MAX = 900f; const val MAX_PLANET_SPEED = 10000f; const val MAX_STAR_SPEED = 2000f; const val WORMHOLE_G = 1500f; const val WORMHOLE_PULL_RANGE = 1200f
    private var thermalTick = 0; private class MutableFloat(var value: Float)
    private val primitiveBuffer = PhysicsPrimitiveBuffer()

    suspend fun applyPhysics(bodies: MutableList<SpaceBody>, stars: MutableList<SpaceBody>, nebulae: List<Nebula>, deltaTime: Float, config: SimulationConfig, pool: QuadNodePool, isStableMode: Boolean = false, rng: Random? = null): List<MergerEvent> {
        val mergerEvents = mutableListOf<MergerEvent>(); val barycenter = calculateBarycenter(stars)
        for (s in stars) {
            var tfx = 0f; var tfy = 0f; for (other in stars) { if (s.id == other.id) continue; val force = calculateGravityDirectRaw(s.position, s.mass, other.position, other.mass, config.g, s.isBlackHole || other.isBlackHole, s.isStar && other.isStar && s.currentlyOverlapping.contains(other.id)); tfx += force.x; tfy += force.y }
            val accX = tfx / s.mass.coerceAtLeast(1f); val accY = tfy / s.mass.coerceAtLeast(1f); val damp = if (stars.size == 1) config.stellarDamping else 1.0f
            s.velocity = Offset((s.velocity.x + accX * deltaTime) * damp, (s.velocity.y + accY * deltaTime) * damp)
            if (config.starsAreDynamic) s.position += s.velocity * deltaTime
            if (s.velocity.getDistance() > MAX_STAR_SPEED) s.velocity = s.velocity.normalized() * MAX_STAR_SPEED
            val distS = (s.position - barycenter).getDistance()
            if (distS > config.auLimit) s.velocity += (barycenter - s.position).normalized() * 60f * deltaTime
            updateStellarEvolution(s, deltaTime, mergerEvents); s.rotation = (s.rotation + s.rotationSpeed * deltaTime) % 360f
        }
        pool.clear(); val tree = buildQuadtree(bodies.filter { it.mass < HEAVY_PLANET_THRESHOLD }, pool); val heavyP = bodies.filter { it.mass >= HEAVY_PLANET_THRESHOLD }
        val mfx = MutableFloat(0f); val mfy = MutableFloat(0f)
        val adaptiveTheta = when {
            bodies.size + stars.size > 400 -> 1.0f
            bodies.size + stars.size > 200 -> 0.8f
            else -> THETA
        }
        primitiveBuffer.syncFrom(bodies)
        for (i in 0 until bodies.size) {
            val b = bodies[i]
            mfx.value = 0f; mfy.value = 0f
            val px = primitiveBuffer.posX[i]
            val py = primitiveBuffer.posY[i]
            val pm = primitiveBuffer.mass[i]
            for (s in stars) { val force = calculateGravityDirectRaw(Offset(px, py), pm, s.position, s.mass, config.g, b.isBlackHole || s.isBlackHole, false); mfx.value += force.x; mfy.value += force.y }
            for (h in heavyP) { if (b.id == h.id) continue; val force = calculateGravityDirectRaw(Offset(px, py), pm, h.position, h.mass, config.g, b.isBlackHole || h.isBlackHole, false); mfx.value += force.x; mfy.value += force.y }
            if (tree != null) { val treeAcc = tree.calculateAcceleration(b, adaptiveTheta, config.g); mfx.value += treeAcc.x * pm; mfy.value += treeAcc.y * pm }
            applyWormholePull(b, config.wormholes, mfx, mfy); applyNebulaEffects(b, nebulae, deltaTime, mfx, mfy)
            
            primitiveBuffer.velX[i] += (mfx.value / pm.coerceAtLeast(1f)) * deltaTime
            primitiveBuffer.velY[i] += (mfy.value / pm.coerceAtLeast(1f)) * deltaTime
            val oldX = primitiveBuffer.posX[i]
            val oldY = primitiveBuffer.posY[i]
            primitiveBuffer.posX[i] += primitiveBuffer.velX[i] * deltaTime
            primitiveBuffer.posY[i] += primitiveBuffer.velY[i] * deltaTime
            
            b.velocity = Offset(primitiveBuffer.velX[i], primitiveBuffer.velY[i])
            val oldPos = Offset(oldX, oldY)
            b.position = Offset(primitiveBuffer.posX[i], primitiveBuffer.posY[i])
            b.rotation = (b.rotation + b.rotationSpeed * deltaTime) % 360f; handleWormholeTeleportation(b, oldPos, config.wormholes)
            if (b.velocity.getDistance() > MAX_PLANET_SPEED) {
                b.velocity = b.velocity.normalized() * MAX_PLANET_SPEED
                primitiveBuffer.velX[i] = b.velocity.x
                primitiveBuffer.velY[i] = b.velocity.y
            }

            val distB = (b.position - barycenter).getDistance()
            if (distB > config.auLimit) {
                val push = (barycenter - b.position).normalized() * 80f * (b.velocity.getDistance() / 4000f).coerceIn(1f, 8f) * (distB / config.auLimit).coerceIn(1f, 5f) * deltaTime
                b.velocity += push
                primitiveBuffer.velX[i] = b.velocity.x
                primitiveBuffer.velY[i] = b.velocity.y
            }
        }
        primitiveBuffer.syncTo(bodies)
        val (finalP, finalS, colEvs) = handleAllCollisionsReliable(bodies, stars, config, config.planetsCanBeConsumed, rng)
        val extEvs = updateHabitability(finalP, finalS, deltaTime, config.evolutionSpeed)
        applyExplosionForces(finalP, finalS, colEvs, isStableMode)
        bodies.clear(); bodies.addAll(finalP); stars.clear(); stars.addAll(finalS)
        mergerEvents.addAll(colEvs); mergerEvents.addAll(extEvs)
        return mergerEvents
    }

    private fun calculateBarycenter(stars: List<SpaceBody>): Offset {
        if (stars.isEmpty()) return Offset.Zero
        var tx = 0f; var ty = 0f; var tm = 0f; for (s in stars) { tx += s.position.x * s.mass; ty += s.position.y * s.mass; tm += s.mass }; return if (tm > 0) Offset(tx / tm, ty / tm) else Offset.Zero
    }

    private fun updateStellarEvolution(s: SpaceBody, dt: Float, events: MutableList<MergerEvent>) {
        if (s.isBlackHole) { s.mass += PASSIVE_ACCRETION_RATE * dt; s.radius = (60f * sqrt(s.mass / 1_000_000f)).coerceAtMost(400f) }
        else {
            s.mass = (s.mass + STAR_ACCRETION_RATE * dt).coerceAtMost(BLACK_HOLE_LIMIT); s.temperature = (3000f + (s.mass / BLACK_HOLE_LIMIT) * 27000f).coerceIn(3000f, 30000f)
            if (s.mass >= BLACK_HOLE_LIMIT) { s.isBlackHole = true; s.radius = 60f; s.color = Color.Black; events.add(MergerEvent(s.position, s.mass, velocity = s.velocity, didCreateBlackHole = true)) }
        }
    }

    private fun applyWormholePull(b: SpaceBody, wormholes: List<Wormhole>, mfx: MutableFloat, mfy: MutableFloat) {
        for (w in wormholes) {
            val dx = w.position.x - b.position.x; val dy = w.position.y - b.position.y; val dSq = dx * dx + dy * dy
            if (dSq in 1f..(WORMHOLE_PULL_RANGE * WORMHOLE_PULL_RANGE)) { val f = (WORMHOLE_G * b.mass) / (dSq + 400f); val dist = sqrt(dSq + 400f); mfx.value += (dx / dist) * f; mfy.value += (dy / dist) * f }
        }
    }

    private fun applyNebulaEffects(b: SpaceBody, nebulae: List<Nebula>, dt: Float, mfx: MutableFloat, mfy: MutableFloat) {
        for (n in nebulae) {
            val dx = n.position.x - b.position.x; val dy = n.position.y - b.position.y; val dist = sqrt(dx * dx + dy * dy)
            if (dist < n.radius) {
                val speed = b.velocity.getDistance(); if (speed > 0.1f) { val drag = speed * n.density * 0.05f; mfx.value -= (b.velocity.x / speed) * drag * b.mass; mfy.value -= (b.velocity.y / speed) * drag * b.mass }
                val accretion = 20f * n.density * dt; b.mass += accretion; b.composition = mergeCompositionsRaw(b.composition, b.mass - accretion, n.composition, accretion); b.radius = (b.radius + (accretion / 200f)).coerceAtMost(250f)
            }
        }
    }

    private fun handleWormholeTeleportation(b: SpaceBody, oldPos: Offset, wormholes: List<Wormhole>) {
        if (wormholes.isEmpty()) return
        for (w in wormholes) {
            if (w.id == b.lastWarpExitId) { if ((b.position - w.position).getDistance() > w.radius + b.radius + 20f) b.lastWarpExitId = null; continue }
            val dx = b.position.x - oldPos.x; val dy = b.position.y - oldPos.y; val wdx = w.position.x - oldPos.x; val wdy = w.position.y - oldPos.y; val lSq = dx*dx + dy*dy; val t = if (lSq > 0.1f) ((wdx*dx + wdy*dy) / lSq).coerceIn(0f, 1f) else 0f
            if (((oldPos.x + dx*t) - w.position.x).pow(2) + ((oldPos.y + dy*t) - w.position.y).pow(2) < (w.radius + b.radius).pow(2)) {
                val exit = wormholes.find { it.id == w.linkedId } ?: continue; val angR = (exit.exitAngle * PI / 180.0).toFloat()
                b.position = exit.position + Offset(cos(angR) * (exit.radius + b.radius + 20f), sin(angR) * (exit.radius + b.radius + 20f)); val speed = b.velocity.getDistance(); b.velocity = Offset(cos(angR) * speed, sin(angR) * speed); b.lastWarpExitId = exit.id; break
            }
        }
    }

    private fun calculateGravityDirectRaw(p1: Offset, m1: Float, p2: Offset, m2: Float, G: Float, hasBH: Boolean, overlap: Boolean): Offset {
        val dx = p2.x - p1.x; val dy = p2.y - p1.y; val dSq = dx * dx + dy * dy; val soft = if (hasBH) 1.0f else 250f
        var force = ( (if (hasBH) G * 2.5f else G) * m1 * m2) / (dSq + soft); if (overlap) force *= (if (hasBH) 0.0f else 0.05f)
        val dist = sqrt(dSq + soft); return Offset((dx / dist) * force, (dy / dist) * force)
    }

    internal fun updateHabitability(bodies: List<SpaceBody>, stars: List<SpaceBody>, dt: Float, speedMult: Float): List<MergerEvent> {
        if (stars.isEmpty()) return emptyList()
        val extinctionEvents = mutableListOf<MergerEvent>()
        val sun = stars.firstOrNull { !it.isBlackHole } ?: stars.first(); val bhs = stars.filter { it.isBlackHole }
        thermalTick++; val updateT = thermalTick % 30 == 0
        for (b in bodies) {
            val dist = (b.position - sun.position).getDistance()
            if (updateT && !b.isStar) b.temperature = (300f * sqrt(650f / dist.coerceAtLeast(100f))) * (1.0f + ((b.id % 20) - 10) / 100f)
            val inZ = dist in HABITABLE_MIN..HABITABLE_MAX; var lp = b.lifeProgress
            if (bhs.any { (b.position - it.position).getDistance() < it.radius * 2.5f }) lp = (lp - 0.5f * dt * speedMult).coerceAtLeast(0f)
            else if (inZ) lp = (lp + 0.05f * dt * speedMult).coerceAtMost(1.0f)
            else lp = (lp - 0.03f * dt * speedMult).coerceAtLeast(0f)
            b.lifeProgress = lp; b.hasLife = lp >= 1.0f || (lp > 0f && b.hasLife); b.minObservedDist = minOf(b.minObservedDist, dist); b.maxObservedDist = maxOf(b.maxObservedDist, dist)
            if (b.hasLife && (b.maxObservedDist - b.minObservedDist) < 50f && dist > 200f) b.alienProgress = (b.alienProgress + 0.03f * dt * speedMult).coerceAtMost(1.0f)
            else b.alienProgress = (b.alienProgress - 0.02f * dt * speedMult).coerceAtLeast(0f)
            b.hasAliens = b.alienProgress >= 1.0f || b.hasAliens

            // Jordy Population Growth & Extinction
            if (b.hasAliens || b.alienProgress > 0.1f) {
                if (b.jordyPopulation == 0L) b.jordyPopulation = 10_000L
                b.jordyPopulation = (b.jordyPopulation * (1.0f + 0.05f * dt * speedMult)).toLong().coerceAtMost(999_999_999_999_999L)
            } else if (b.jordyPopulation > 0L && lp == 0f) {
                val lost = b.jordyPopulation
                b.jordyPopulation = 0L
                extinctionEvents.add(MergerEvent(b.position, b.mass, lostJordyPopulation = lost))
            }
        }
        return extinctionEvents
    }

    private fun buildQuadtree(bodies: List<SpaceBody>, pool: QuadNodePool): QuadNode? {
        if (bodies.isEmpty()) return null
        var minX = Float.MAX_VALUE; var minY = Float.MAX_VALUE; var maxX = -Float.MAX_VALUE; var maxY = -Float.MAX_VALUE
        for (b in bodies) { minX = minOf(minX, b.position.x); minY = minOf(minY, b.position.y); maxX = maxOf(maxX, b.position.x); maxY = maxOf(maxY, b.position.y) }
        val range = maxOf(maxX - minX, maxY - minY, 1000f); val root = pool.get().apply { reset(Rect(minX - 100f, minY - 100f, minX + range + 100f, minY + range + 100f)) }
        for (b in bodies) root.insert(b, pool); return root
    }

    private fun applyExplosionForces(planets: List<SpaceBody>, stars: List<SpaceBody>, events: List<MergerEvent>, isStable: Boolean) {
        for (e in events) {
            if (!e.isStellarEvent) continue
            val massScale = (e.totalMass / 1_000_000f).coerceAtLeast(1f); val force = (if (e.didCreateBlackHole) 12000f * massScale else 6000f) * (if (isStable) 0.4f else 1.0f)
            for (p in planets) { val diff = p.position - e.position; val d = diff.getDistance(); if (d in 1f..8000f) p.velocity += diff.normalized() * ((force * (1f - d / 8000f)) / sqrt(p.mass.coerceAtLeast(1f))) }
            for (s in stars) { val diff = s.position - e.position; val d = diff.getDistance(); if (d in 1f..8000f) s.velocity += diff.normalized() * ((force * 0.3f * (1f - d / 8000f)) / sqrt(s.mass.coerceAtLeast(1f))) }
        }
    }

    internal fun handleAllCollisionsReliable(planets: List<SpaceBody>, stars: List<SpaceBody>, config: SimulationConfig, planetsCanBeConsumed: Boolean, rng: Random?): Triple<List<SpaceBody>, List<SpaceBody>, List<MergerEvent>> {
        val nextS = stars.toMutableList(); val events = mutableListOf<MergerEvent>()
        if (config.starsAreDynamic && config.starsCanMerge) {
            val toRemS = mutableSetOf<Int>()
            for (i in nextS.indices) {
                if (i in toRemS) continue
                for (j in i + 1 until nextS.size) {
                    if (i in toRemS || j in toRemS) continue
                    val s1 = nextS[i]; val s2 = nextS[j]; val dist = (s1.position - s2.position).getDistance(); val cr = s1.radius + s2.radius
                    if (dist < cr) {
                        val isBH = s1.isBlackHole || s2.isBlackHole; val overF = s1.overlapFrameCount[s2.id] ?: 0
                        if (!s1.currentlyOverlapping.contains(s2.id) || isBH || (dist < cr * 0.7f && overF > 7200) || (overF > 90 && dist < cr * 0.15f)) {
                            if ((s1.closePassCount[s2.id] ?: 0) < config.mergerPassLimit && !isBH && overF < 90) { s1.closePassCount = s1.closePassCount + (s2.id to (s1.closePassCount[s2.id] ?: 0) + 1); s1.currentlyOverlapping = s1.currentlyOverlapping + s2.id; s2.closePassCount = s2.closePassCount + (s1.id to (s2.closePassCount[s1.id] ?: 0) + 1); s2.currentlyOverlapping = s2.currentlyOverlapping + s1.id; continue }
                            val tm = s1.mass + s2.mass; s1.position = (s1.position * s1.mass + s2.position * s2.mass) / tm.coerceAtLeast(1f); s1.velocity = (s1.velocity * s1.mass + s2.velocity * s2.mass) / tm.coerceAtLeast(1f); s1.mass = tm; s1.isBlackHole = isBH || tm >= (BLACK_HOLE_LIMIT - 10f); s1.color = if (s1.isBlackHole) Color.Black else s1.color; s1.composition = mergeCompositionsRaw(s1.composition, s1.mass - s2.mass, s2.composition, s2.mass); s1.radius = if (s1.isBlackHole) (60f * sqrt(tm / 1_000_000f)).coerceAtMost(400f) else (s1.radius + s2.radius * 0.2f).coerceAtMost(250f); s1.closePassCount = emptyMap(); s1.currentlyOverlapping = emptySet(); s1.overlapFrameCount = emptyMap(); toRemS.add(j); events.add(MergerEvent(s1.position, tm, isStellarEvent = true, consumedIds = listOf(s1.id, s2.id), producedIds = listOf(s1.id)))
                        } else if (dist < cr * 0.7f) { s1.overlapFrameCount = s1.overlapFrameCount + (s2.id to overF + 1); s2.overlapFrameCount = s2.overlapFrameCount + (s1.id to overF + 1) }
                    } else if (s1.currentlyOverlapping.contains(s2.id)) { s1.currentlyOverlapping = s1.currentlyOverlapping - s2.id; s2.currentlyOverlapping = s2.currentlyOverlapping - s1.id }
                }
            }
            val filteredS = nextS.filterIndexed { index, _ -> index !in toRemS }; nextS.clear(); nextS.addAll(filteredS)
        }
        val toRemP = mutableSetOf<Int>(); val newP = mutableListOf<SpaceBody>()
        for (i in planets.indices) {
            if (i in toRemP) continue
            for (j in i + 1 until planets.size) {
                if (i in toRemP || j in toRemP) continue
                val p1 = planets[i]; val p2 = planets[j]; val cr = p1.radius + p2.radius
                if (abs(p1.position.x - p2.position.x) < cr && abs(p1.position.y - p2.position.y) < cr && (p1.position - p2.position).getDistance() < cr) {
                    val tm = p1.mass + p2.mass; val nv = (p1.velocity * p1.mass + p2.velocity * p2.mass) / tm.coerceAtLeast(1f); val np = (p1.position * p1.mass + p2.position * p2.mass) / tm.coerceAtLeast(1f)
                    if (tm >= (BLACK_HOLE_LIMIT - 10f)) { toRemP.add(i); toRemP.add(j); val nId = Random.nextLong(); events.add(MergerEvent(np, tm, velocity = nv, didCreateBlackHole = true, consumedIds = listOf(p1.id, p2.id), producedIds = listOf(nId))) }
                    else if (tm >= STELLAR_EVOLUTION_LIMIT) { toRemP.add(i); toRemP.add(j); val nId = rng?.nextLong() ?: Random.nextLong(); events.add(MergerEvent(np, tm, velocity = nv, didCreateStar = true, isStellarEvent = true, consumedIds = listOf(p1.id, p2.id), producedIds = listOf(nId))) }
                    else {
                        val rv = (p1.velocity - p2.velocity).getDistance(); val hE = ((p1.mass * p2.mass) / tm.coerceAtLeast(1f)) * rv * rv > (tm * 6_000_000f)
                        if (hE && (rng?.nextFloat() ?: Random.nextFloat()) < 0.3f && (tm / 3f) >= 2000f) {
                            val fIds = mutableListOf<Long>(); val impactDir = (p2.position - p1.position).normalized(); val tangent = Offset(-impactDir.y, impactDir.x); val spinK = ((p1.velocity - p2.velocity).x * tangent.x + (p1.velocity - p2.velocity).y * tangent.y) / p1.radius * 0.1f
                            repeat(3) { idx ->
                                val fid = rng?.nextLong() ?: Random.nextLong(); if (idx == 0) fIds.add(fid); val ang = (idx.toFloat() / 3f) * 2f * PI.toFloat(); val kd = Offset(cos(ang).toFloat(), sin(ang).toFloat()); newP.add(SpaceBody(id = fid, name = "${p1.name}-$idx", position = np + kd * p1.radius, velocity = nv + kd * (rv * 0.15f).coerceIn(500f, 3000f), mass = tm * 0.333f, radius = p1.radius * 0.7f, color = p1.color, composition = p1.composition, rotationSpeed = (p1.rotationSpeed + spinK * (if (idx == 0) 1.5f else 0.8f)).coerceIn(-360f, 360f), hasRings = if ((rng?.nextFloat() ?: Random.nextFloat()) < 0.05f) p1.hasRings else false, parentIds = listOf(p1.id, p2.id)))
                            }
                            toRemP.add(i); toRemP.add(j); events.add(MergerEvent(np, tm, consumedIds = listOf(p1.id, p2.id), producedIds = fIds))
                        } else { p1.position = np; p1.velocity = nv; p1.mass = tm; p1.radius = sqrt(p1.radius * p1.radius + p2.radius * p2.radius).coerceAtMost(250f); p1.hasRings = p1.hasRings || p2.hasRings; p1.parentIds = (p1.parentIds + listOf(p1.id, p2.id)).distinct(); p1.composition = mergeCompositionsRaw(p1.composition, p1.mass - p2.mass, p2.composition, p2.mass); toRemP.add(j); events.add(MergerEvent(np, tm, consumedIds = listOf(p1.id, p2.id), producedIds = listOf(p1.id))) }
                    }
                }
            }
        }
        var finalP = planets.filterIndexed { idx, _ -> idx !in toRemP }.toMutableList(); finalP.addAll(newP)
        for (star in nextS) {
            val toRem = mutableSetOf<Int>(); val newF = mutableListOf<SpaceBody>()
            for (j in finalP.indices) {
                val p = finalP[j]; val d = (star.position - p.position).getDistance()
                if (planetsCanBeConsumed && d < (star.radius + p.radius) * 1.05f) {
                    val lostJ = p.jordyPopulation; p.jordyPopulation = 0L
                    star.mass += p.mass; if (star.isBlackHole) star.radius = (60f * sqrt(star.mass / 1_000_000f)).coerceAtMost(400f); star.composition = mergeCompositionsRaw(star.composition, star.mass - p.mass, p.composition, p.mass); toRem.add(j); events.add(MergerEvent(p.position, p.mass, lostJordyPopulation = lostJ, consumedIds = listOf(p.id), producedIds = listOf(star.id)))
                }
                else if (star.isBlackHole && planetsCanBeConsumed && p.mass >= 200f && d < star.radius * 3.0f && (rng?.nextFloat() ?: Random.nextFloat()) < 0.15f) {
                    val lostJ = p.jordyPopulation; p.jordyPopulation = 0L
                    val cM = p.mass * 0.33f; val fM = (p.mass - cM) / 2f; star.mass += cM; star.radius = (60f * sqrt(star.mass / 1_000_000f)).coerceAtMost(400f); val dir = (p.position - star.position).normalized(); val perp = Offset(-dir.y, dir.x); val id1 = rng?.nextLong() ?: Random.nextLong(); val id2 = rng?.nextLong() ?: Random.nextLong(); val kick = 1000f * (star.mass / 1e6f).coerceAtLeast(1f)
                    newF.add(p.copy(id = id1, name = "${p.name}-Debris", mass = fM, radius = p.radius * 0.7f, position = p.position + perp * 20f, velocity = p.velocity + perp * kick)); newF.add(p.copy(id = id2, name = "${p.name}-Fragment", mass = fM, radius = p.radius * 0.7f, position = p.position - perp * 20f, velocity = p.velocity - perp * kick)); toRem.add(j); events.add(MergerEvent(p.position, cM, lostJordyPopulation = lostJ, consumedIds = listOf(p.id), producedIds = listOf(id1, id2, star.id)))
                }
            }
            finalP = finalP.filterIndexed { idx, _ -> idx !in toRem }.toMutableList(); finalP.addAll(newF)
        }
        for (e in events) {
            if (e.producedIds.isNotEmpty() && (e.didCreateBlackHole || e.didCreateStar)) {
                val nid = e.producedIds.first()
                if (e.didCreateBlackHole) nextS.add(SpaceBody(id = nid, name = "Singularity-${Random.nextInt(100, 999)}", position = e.position, velocity = e.velocity, mass = e.totalMass, radius = 60f, color = Color.Black, isStar = true, isBlackHole = true, composition = mapOf("Dark Matter" to 1.0f)))
                else if (e.didCreateStar) nextS.add(SpaceBody(id = nid, name = "Proto-Star-${Random.nextInt(100, 999)}", position = e.position, velocity = e.velocity, mass = (e.totalMass * 1.5f).coerceAtMost(BLACK_HOLE_LIMIT - 10f), radius = 120f, color = Color.Yellow, isStar = true, temperature = 6000f, composition = mapOf("Hydrogen" to 0.75f, "Helium" to 0.25f)))
            }
        }
        finalP.removeAll { it.position.x.isNaN() }; nextS.removeAll { it.position.x.isNaN() }; return Triple(finalP, nextS, events.toList())
    }

    suspend fun calculateTrajectories(planets: List<SpaceBody>, stars: List<SpaceBody>, config: SimulationConfig, pool: QuadNodePool): OracleResult {
        val steps = 250; val dt = 0.0166f; val paths = List(planets.size) { mutableListOf<Offset>() }; val cols = mutableListOf<Offset>()
        val tempP = planets.map { it.copy() }; val tempS = stars.map { it.copy() }; val dead = BooleanArray(planets.size) { false }
        repeat(steps) {
            val bary = calculateBarycenter(tempS); pool.clear(); val tree = buildQuadtree(tempP.filterIndexed { idx, p -> !dead[idx] && p.mass < HEAVY_PLANET_THRESHOLD }, pool); val heavy = tempP.filterIndexed { idx, p -> !dead[idx] && p.mass >= HEAVY_PLANET_THRESHOLD }
            for (i in tempP.indices) {
                val p = tempP[i]; if (dead[i]) continue
                var tfx = 0f; var tfy = 0f
                for (s in tempS) { val force = calculateGravityDirectRaw(p.position, p.mass, s.position, s.mass, config.g, p.isBlackHole || s.isBlackHole, false); tfx += force.x; tfy += force.y; if (config.planetsCanBeConsumed && (p.position - s.position).getDistance() < s.radius + p.radius) dead[i] = true }
                if (dead[i]) continue
                for (h in heavy) if (p.id != h.id) { val force = calculateGravityDirectRaw(p.position, p.mass, h.position, h.mass, config.g, false, false); tfx += force.x; tfy += force.y }
                if (tree != null) { val a = tree.calculateAcceleration(p, THETA, config.g); tfx += a.x * p.mass; tfy += a.y * p.mass }
                val nv = Offset(p.velocity.x + (tfx / p.mass.coerceAtLeast(1f)) * dt, p.velocity.y + (tfy / p.mass.coerceAtLeast(1f)) * dt); val np = Offset(p.position.x + nv.x * dt, p.position.y + nv.y * dt); p.velocity = nv; p.position = np
                val dB = (np - bary).getDistance(); if (dB > config.auLimit) p.velocity += (bary - np).normalized() * 80f * (p.velocity.getDistance() / 4000f).coerceIn(1f, 8f) * (dB / config.auLimit).coerceIn(1f, 5f) * dt
                if (it % 2 == 0) paths[i].add(np)
            }

            for (i in tempP.indices) {
                if (dead[i]) continue
                for (j in i + 1 until tempP.size) {
                    if (dead[j]) continue
                    val p1 = tempP[i]; val p2 = tempP[j]
                    val dist = (p1.position - p2.position).getDistance()
                    if (dist < p1.radius + p2.radius) {
                        cols.add((p1.position + p2.position) / 2f)
                        dead[i] = true; dead[j] = true
                    }
                }
            }

            if (config.starsAreDynamic) for (s in tempS) {
                var tfx = 0f; var tfy = 0f; for (o in tempS) if (s.id != o.id) { val force = calculateGravityDirectRaw(s.position, s.mass, o.position, o.mass, config.g, s.isBlackHole || o.isBlackHole, false); tfx += force.x; tfy += force.y }
                val nv = Offset((s.velocity.x + (tfx / s.mass.coerceAtLeast(1f)) * dt) * (if (tempS.size == 1) config.stellarDamping else 1.0f), (s.velocity.y + (tfy / s.mass.coerceAtLeast(1f)) * dt) * (if (tempS.size == 1) config.stellarDamping else 1.0f)); val np = Offset(s.position.x + nv.x * dt, s.position.y + nv.y * dt); s.velocity = nv; s.position = if (np.getDistance() > 10000f) np + (-np).normalized() * 60f * dt else np
            }
        }
        return OracleResult(paths, cols)
    }

    private fun Offset.normalized() = let { val d = getDistance(); if (d > 0) this / d else Offset.Zero }
    private fun Offset.getDistance() = sqrt((x * x + y * y).toDouble()).toFloat()
    private fun mergeCompositionsRaw(c1: Map<String, Float>, m1: Float, c2: Map<String, Float>, m2: Float): Map<String, Float> {
        val tm = m1 + m2; return (c1.keys + c2.keys).associateWith { k -> ((c1[k] ?: 0f) * m1 + (c2[k] ?: 0f) * m2) / tm.coerceAtLeast(1f) }
    }
}
